/**
 * rtc-ws-negotiate.ts — SDP-over-WebSocket negotiation for voice calls.
 *
 * Voice-call setup used to POST the WebRTC SDP offer to `/api/rtc/call`
 * (REST) exclusively — see rtc-call.ts's header. That breaks on multi-task
 * backends: the POST is a brand-new HTTP request with no session affinity,
 * so it can land on a task that doesn't own this session's realtime
 * connection. The fix carries the offer over the assistant WebSocket
 * instead — always routed to the task that owns the session — with
 * automatic fallback to the REST path when talking to an older backend
 * that has no handler for the `rtc_call` message type at all (and so would
 * otherwise just silently never respond).
 *
 * Framework-free — no React — same discipline as rtc-call.ts, so this is
 * trivially testable and reusable outside any hook.
 *
 * Module-level pending-request registry: `negotiateOverWs` mints a
 * `request_id`, registers a pending entry keyed by it, and sends the
 * `rtc_call` frame. `handleRtcWsEvent` (called by the connection driver,
 * see useAssistantConnection.ts) routes inbound rtc_call_ack/answer/failed
 * frames back to the matching entry by request_id. The registry is
 * module-level (not per-hook-instance) because the WS driver and the call
 * hook are different modules that don't otherwise share a reference to
 * "the current negotiation" — same reason rtc-call.ts's negotiate() is a
 * free function rather than a class instance.
 *
 * Abandonment is symmetric with negotiation: whenever the frontend gives up
 * on a pending negotiation WITHOUT the backend having told it to (ack
 * timeout, answer timeout, or the socket dying outright — see
 * `rejectAllPending`), it tells the backend so via `rtc_call_cancel`, so a
 * negotiation the frontend has moved on from can never leave an attached
 * call orphaned server-side. See RtcCallCancelMessage's docstring
 * (aol_schemas.ws_events) for the exact backend guarantee this buys.
 */

import { WSEventType } from '@/types/chat';
// Imported from the dependency-free rtc-errors.ts, NOT from rtc-call.ts —
// rtc-call.ts imports negotiateOverWs/RtcWsNoAckError FROM this file, so
// importing RtcCallTimeoutError back from rtc-call.ts would be a circular
// import between the two modules. See rtc-errors.ts's header.
import { RtcCallTimeoutError } from './rtc-errors';

/** Absence of `rtc_call_ack` within this long means the backend predates
 *  WebRTC-over-WS entirely (no handler for `rtc_call` at all) — the caller
 *  falls back to the legacy REST `/api/rtc/call` path. */
export const RTC_WS_ACK_TIMEOUT_MS = 4_000;

/** Parity with rtc-call.ts's RTC_CALL_TIMEOUT_MS — once the backend has
 *  acked (so it's definitely working on this offer), the same 15s budget
 *  applies to the OpenAI Realtime Calls API round-trip before giving up. */
export const RTC_WS_ANSWER_TIMEOUT_MS = 15_000;

/** Thrown when no `rtc_call_ack` arrives within RTC_WS_ACK_TIMEOUT_MS — the
 *  signal to abandon the WS path and fall back to REST (old backend). */
export class RtcWsNoAckError extends Error {
  constructor() {
    super(
      '[rtc-ws] no rtc_call_ack within RTC_WS_ACK_TIMEOUT_MS — backend likely predates WebRTC-over-WS'
    );
    this.name = 'RtcWsNoAckError';
  }
}

/** Thrown on an explicit `rtc_call_failed` frame — the backend understood
 *  the offer and already attempted it, so (unlike RtcWsNoAckError) this is
 *  NOT a fall-back-to-REST signal; a REST retry would race a possibly
 *  still-completing attach. `reason` is the backend's RtcCallFailureReason
 *  ('upstream_error' | 'attach_failed'), surfaced as a plain string so
 *  callers aren't forced to import the enum just to log/classify it. */
export class RtcWsNegotiateError extends Error {
  constructor(public reason: string) {
    super(`[rtc-ws] rtc_call_failed: ${reason}`);
    this.name = 'RtcWsNegotiateError';
  }
}

interface PendingNegotiation {
  resolve: (result: { sdp: string; callId: string }) => void;
  reject: (err: Error) => void;
  /** The negotiation's own WS send function, kept on the entry (not just in
   *  negotiateOverWs's closure) so the ANSWER-timeout path — armed from
   *  handleRtcWsEvent, a different function than negotiateOverWs — can still
   *  send `rtc_call_cancel` when it gives up. The ACK-timeout path already
   *  has `send` via closure and doesn't need this, but reads the same
   *  reference for consistency. */
  send: (json: string) => void;
  ackTimer: ReturnType<typeof setTimeout> | null;
  answerTimer: ReturnType<typeof setTimeout> | null;
}

/** Keyed by request_id. Module-level by design — see the file header. */
const pending = new Map<string, PendingNegotiation>();

/** Clears both timers and drops the entry. Idempotent (a no-op if already
 *  settled) — every settle path (ack-timeout, answer-timeout, answer,
 *  failed, rejectAllPending) calls this before resolving/rejecting so a
 *  request_id is never left dangling in the registry after its promise
 *  settles. */
function settle(requestId: string): void {
  const entry = pending.get(requestId);
  if (!entry) return;
  if (entry.ackTimer) clearTimeout(entry.ackTimer);
  if (entry.answerTimer) clearTimeout(entry.answerTimer);
  pending.delete(requestId);
}

/**
 * Sends `rtc_call_cancel` for `requestId`, swallowing any send failure.
 * Called right before rejecting a negotiation the frontend is about to
 * abandon (ack-timeout, answer-timeout) — the socket may already be dying at
 * that exact moment (that's sometimes WHY the timeout fired), so a failed
 * cancel send must never mask or replace the timeout rejection the caller is
 * about to throw; it's best-effort notice to the backend, not something the
 * frontend's own recovery depends on. See RtcCallCancelMessage's docstring
 * (aol_schemas.ws_events) for the backend contract this frame guarantees.
 */
function sendCancel(requestId: string, send: (json: string) => void): void {
  try {
    send(
      JSON.stringify({
        type: WSEventType.RTC_CALL_CANCEL,
        request_id: requestId,
      })
    );
  } catch {
    // Best-effort — see this function's doc comment.
  }
}

/**
 * Sends the SDP offer over the assistant WebSocket and resolves with the
 * backend's answer, or rejects with one of:
 *   - RtcWsNoAckError    — no ack within RTC_WS_ACK_TIMEOUT_MS (old backend;
 *                          caller should fall back to REST).
 *   - RtcCallTimeoutError — acked, but no answer within
 *                          RTC_WS_ANSWER_TIMEOUT_MS (parity with the REST
 *                          path's own timeout classification).
 *   - RtcWsNegotiateError — an explicit rtc_call_failed frame.
 *
 * `send` is the caller's WebSocket send function (kept generic — this
 * module doesn't hold a reference to the socket itself, mirroring how
 * useCallMode already sends one-shot control frames directly via wsRef).
 */
export function negotiateOverWs(
  send: (json: string) => void,
  sdp: string
): Promise<{ sdp: string; callId: string }> {
  const requestId = crypto.randomUUID();

  return new Promise((resolve, reject) => {
    const entry: PendingNegotiation = {
      resolve,
      reject,
      send,
      ackTimer: null,
      answerTimer: null,
    };
    entry.ackTimer = setTimeout(() => {
      settle(requestId);
      // A slow-but-current backend must not complete a second, competing
      // call while we fall back to REST (see RtcCallCancelMessage's
      // docstring, aol_schemas.ws_events, producer #1).
      sendCancel(requestId, send);
      reject(new RtcWsNoAckError());
    }, RTC_WS_ACK_TIMEOUT_MS);
    pending.set(requestId, entry);

    send(
      JSON.stringify({ type: WSEventType.RTC_CALL, request_id: requestId, sdp })
    );
  });
}

/**
 * Routes an inbound `rtc_call_ack` / `rtc_call_answer` / `rtc_call_failed`
 * frame to its pending negotiation by `request_id`. Called from the
 * connection driver's handleServerEvent (see useAssistantConnection.ts) —
 * these are pure transport-level frames, never touching ChatContext or the
 * reducer.
 *
 * Returns whether the event was consumed. A missing/unmatched request_id
 * returns false and is a safe no-op (just logged) — this is the EXPECTED
 * shape of a late frame arriving after its own ack/answer timeout already
 * settled the promise (e.g. a slow rtc_call_answer landing just after
 * RTC_WS_ANSWER_TIMEOUT_MS gave up), or of a frame belonging to a
 * negotiation this tab never started.
 */
export function handleRtcWsEvent(event: {
  type: string;
  request_id?: string;
  sdp?: string;
  call_id?: string;
  reason?: string;
}): boolean {
  const requestId = event.request_id;
  const entry = requestId ? pending.get(requestId) : undefined;
  if (!entry) {
    console.debug(
      `[rtc-ws] ignoring ${event.type} for unknown/late request_id=${requestId ?? '(none)'}`
    );
    return false;
  }

  switch (event.type) {
    case WSEventType.RTC_CALL_ACK: {
      // Ack only proves the backend understands `rtc_call` and has started
      // work — clear the "is this an old backend?" timer and start the
      // "did the call actually go through?" one instead.
      if (entry.ackTimer) {
        clearTimeout(entry.ackTimer);
        entry.ackTimer = null;
      }
      entry.answerTimer = setTimeout(() => {
        settle(requestId);
        // The backend may still complete this call within its OWN 30s
        // budget after our 15s gives up — without this, a late-completing
        // negotiation could attach a call to a pc we're about to close (see
        // RtcCallCancelMessage's docstring, aol_schemas.ws_events, producer
        // #2).
        sendCancel(requestId, entry.send);
        // WS-specific message so logs never blame the REST /api/rtc/call
        // endpoint for a WS-transport failure (see rtc-errors.ts).
        entry.reject(
          new RtcCallTimeoutError(
            '[rtc-ws] no rtc_call_answer within RTC_WS_ANSWER_TIMEOUT_MS after ack'
          )
        );
      }, RTC_WS_ANSWER_TIMEOUT_MS);
      return true;
    }
    case WSEventType.RTC_CALL_ANSWER: {
      // Resolves even if no ack was ever seen for this request_id — ack
      // loss (vs. the call itself failing) shouldn't kill a call that
      // actually succeeded.
      settle(requestId);
      entry.resolve({ sdp: event.sdp ?? '', callId: event.call_id ?? '' });
      return true;
    }
    case WSEventType.RTC_CALL_FAILED: {
      settle(requestId);
      entry.reject(new RtcWsNegotiateError(event.reason ?? 'unknown'));
      return true;
    }
    default:
      return false;
  }
}

/**
 * Settles EVERY pending negotiation at once — called once per socket
 * close/error from useAssistantConnection.ts, since a dead socket means
 * NONE of these requests will ever see their ack/answer/failed frame.
 * Deliberately asymmetric by how far each entry got, mirroring the
 * cancel-vs-no-cancel split above:
 *
 *   - Still waiting on ack (no proof the backend ever saw the offer) →
 *     RtcWsNoAckError, the SAME "fall back to REST" signal a real ack
 *     timeout produces. This is safe specifically BECAUSE the socket is
 *     gone: no `rtc_call_cancel` could reach the backend over it anyway,
 *     but the backend's OWN socket-drop teardown will hang up any call it
 *     might still complete for this session — so a competing REST attempt
 *     can't leave a stray duplicate live.
 *   - Past ack (answer pending — the backend DID see the offer and may be
 *     mid-attach) → RtcWsNegotiateError('socket_closed'), NOT a fall-back
 *     signal. No REST retry: the backend's socket-drop teardown already
 *     hangs up whatever this negotiation attaches, so retrying via REST
 *     would race that teardown into a second, competing call — the exact
 *     hazard the explicit-failure (non-ack-timeout) rejections above are
 *     designed to avoid.
 *
 * The registry is empty once this returns (every entry is settle()'d) —
 * safe to call redundantly from both the close and error handlers, or on a
 * socket with nothing pending (a no-op).
 */
export function rejectAllPending(): void {
  for (const [requestId, entry] of Array.from(pending.entries())) {
    // ackTimer is nulled the moment RTC_CALL_ACK clears it (see the
    // RTC_CALL_ACK case above) — its absence is exactly "past ack".
    const pastAck = entry.ackTimer == null;
    settle(requestId);
    if (pastAck) {
      entry.reject(new RtcWsNegotiateError('socket_closed'));
    } else {
      entry.reject(new RtcWsNoAckError());
    }
  }
}
